import type { FC } from "react";
import { useState } from "react";
import styled from "styled-components";
import { i18nFilter } from "app/filters/i18nFilter";
import { setFontVariant, colorsPalettes } from "@similarweb/styles";
import type { ITraffic } from "app/pages/industry-analysis/market-builder/tabs/industry/table/types";
import { RANGE } from "app/pages/industry-analysis/market-builder/tabs/industry/table/constants";
import { FlexColumn, BaseFlex, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { Button } from "@similarweb/ui-components";
import { ToggleFilter } from "pro-features/components/filtersPanel/src/ToggleFilter";
import { allTrackers } from "app/services/track/track";
import { NumberRange } from "app/pages/industry-analysis/market-builder/filters/AllRangeGenericFilters/NumberRange";
import { getCommonIntervals } from "app/pages/industry-analysis/market-builder/filters/AllRangeGenericFilters/fixedValues";
import type { INumberRange } from "app/UtilitiesAndConstants/types/range";

const { carbon } = colorsPalettes;

export interface ITrafficFilter {
    traffic: ITraffic;
    setTraffic: (traffic: ITraffic) => void;
}

const Container = styled(FlexColumn)`
    width: 100%;
`;

const FiltersContainer = styled(FlexColumn)`
    gap: 16px;
    padding: 16px 12px;
`;

const Footer = styled(BaseFlex)`
    border-top: 1px solid ${carbon[100]};
    justify-content: flex-end;
    padding: 8px;
`;

const Label = styled.label`
    ${setFontVariant("caption", carbon[500])};
`;

const RangeContainer = styled.div`
    padding: 0px 4px;
`;

const UniqueToggleContainer = styled(FlexRow)`
    padding: 6px 0px;
    justify-content: space-between;
    align-items: center;
`;

export const TrafficFilter: FC<ITrafficFilter> = (props) => {
    const { traffic, setTraffic } = props;
    const { monthlyVisits, uniqueUsers, isUniqueToggle } = traffic;
    const [monthlyVisitsView, setMonthlyVisitsView] = useState<INumberRange>(monthlyVisits);
    const [uniqueUsersView, setUniqueUsersView] = useState<INumberRange>(uniqueUsers);
    const [isUniqueToggleView, setIsUniqueToggleView] = useState<boolean>(isUniqueToggle);
    const i18n = i18nFilter();
    const { MONTHLY_VISITS, UNIQUE_USERS } = RANGE;
    const submit = () => {
        const monthlyVisits = monthlyVisitsView;
        const uniqueUsers = uniqueUsersView;
        const isUniqueToggle = isUniqueToggleView;
        setTraffic({ monthlyVisits, uniqueUsers, isUniqueToggle });
    };
    const onUniqueToggleClick = () => {
        allTrackers.trackEvent("toggle", "switch", `By industry/UVchange/${isUniqueToggleView ? "on" : "off"}`);
        setIsUniqueToggleView(!isUniqueToggleView);
    };

    const onMonthlyVisitsViewChanged = (value) => {
        allTrackers.trackEvent("Button", "click", `By industry/TrafficAndEngagement/monthlyVisits`);
        setMonthlyVisitsView(value);
    };

    const onUniqueUsersViewChanged = (value) => {
        allTrackers.trackEvent("Button", "click", `By industry/TrafficAndEngagement/uniqueVisitors`);
        setUniqueUsersView(value);
    };

    return (
        <Container>
            <FiltersContainer>
                <RangeContainer>
                    <NumberRange
                        filterValue={monthlyVisitsView}
                        textKey={i18n("industry.research.market.builder.TAndEFilters.monthlyVisits")}
                        onChange={onMonthlyVisitsViewChanged}
                        min={MONTHLY_VISITS.min}
                        max={MONTHLY_VISITS.max}
                    />
                </RangeContainer>
                <RangeContainer>
                    <NumberRange
                        filterValue={uniqueUsersView}
                        textKey={i18n("industry.research.market.builder.TAndEFilters.uniqueVisitors")}
                        onChange={onUniqueUsersViewChanged}
                        min={UNIQUE_USERS.min}
                        max={UNIQUE_USERS.max}
                        fixedValues={getCommonIntervals().filter((interval) => interval <= UNIQUE_USERS.max)}
                    />
                </RangeContainer>

                <UniqueToggleContainer>
                    <Label>{i18n("industry.research.market.builder.unique.toggle.label")}</Label>
                    <ToggleFilter
                        isActive={isUniqueToggleView}
                        onClick={onUniqueToggleClick}
                    />
                </UniqueToggleContainer>
            </FiltersContainer>
            <Footer>
                <Button onClick={submit}>{i18n("common.done")}</Button>
            </Footer>
        </Container>
    );
};
